/**
 * Users List Page
 *
 * Displays a list of all platform users with search, filtering, and pagination.
 * Only accessible by ADMIN and SADMIN roles.
 */

import { useState, useCallback, useMemo, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { User, Plus, Mail, Phone } from 'lucide-react';
import type { ColumnDef, SortingState } from '@tanstack/react-table';

import {
  DataTable,
  Button,
  Badge,
  Card,
  CardHeader,
  CardBody,
} from '@/components/common';
import { userService } from '@/api/services';
import type { PlatformUser, ListRequest } from '@/api/types';
import { formatDate } from '@/utils/date.utils';
import { useToast } from '@/hooks/useToast';
import { useCan } from '@/hooks/usePermissions';

// ============================================
// Page Component
// ============================================

export function UsersListPage() {
  const navigate = useNavigate();
  const toast = useToast();
  // Check if user can create users (dynamic RBAC)
  const canCreateUser = useCan('orgUser.user.manage');

  // State
  const [data, setData] = useState<PlatformUser[]>([]);
  const [totalRows, setTotalRows] = useState(0);
  const [isLoading, setIsLoading] = useState(false);
  const [pagination, setPagination] = useState({ pageIndex: 0, pageSize: 10 });
  const [sorting, setSorting] = useState<SortingState>([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState('');

  // Fetch data
  const fetchUsers = useCallback(
    async (params: { pageIndex: number; pageSize: number; sorting?: SortingState; search?: string }) => {
      setIsLoading(true);
      try {
        const request: ListRequest = {
          paging: {
            page: params.pageIndex + 1,
            size: params.pageSize,
          },
          sorting:
            params.sorting && params.sorting.length > 0
              ? { [params.sorting[0].id]: params.sorting[0].desc ? 'desc' : 'asc' }
              : undefined,
          filtering: params.search ? { username: params.search } : undefined,
        };

        const result = await userService.getUserList(request);
        setData(result.list);
        setTotalRows(result.filteredRecords);
      } catch (error) {
        console.error('Failed to fetch users:', error);
        toast.error('Failed to load users');
      } finally {
        setIsLoading(false);
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    []
  );

  // Debounce search term
  useEffect(() => {
    // If search is cleared, update immediately
    if (searchTerm === '') {
      setDebouncedSearchTerm('');
      return;
    }

    const timer = setTimeout(() => {
      setDebouncedSearchTerm(searchTerm);
    }, 500);

    return () => clearTimeout(timer);
  }, [searchTerm]);

  // Initial load
  useEffect(() => {
    fetchUsers({ ...pagination, sorting, search: debouncedSearchTerm });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pagination, sorting, debouncedSearchTerm]);

  // Handle pagination change
  const handlePaginationChange = useCallback(
    (newPagination: { pageIndex: number; pageSize: number }) => {
      setPagination(newPagination);
    },
    []
  );

  // Handle sorting change
  const handleSortingChange = useCallback((newSorting: SortingState) => {
    setSorting(newSorting);
  }, []);

  // Handle search change
  const handleSearchChange = useCallback(
    (value: string) => {
      setSearchTerm(value);
      setPagination((prev) => (prev.pageIndex === 0 ? prev : { ...prev, pageIndex: 0 }));
    },
    []
  );

  // Navigate to details
  const handleViewDetails = useCallback(
    (user: PlatformUser) => {
      navigate(`/users/${user.id}`);
    },
    [navigate]
  );

  // Navigate to create
  const handleCreateUser = useCallback(() => {
    navigate('/users/create');
  }, [navigate]);

  // Column definitions
  const columns = useMemo<ColumnDef<PlatformUser>[]>(
    () => [
      {
        accessorKey: 'username',
        header: 'Username',
        cell: ({ row }) => (
          <div className="flex items-center gap-2">
            <div className="flex h-8 w-8 items-center justify-center rounded-full bg-primary-100 dark:bg-primary-900/30">
              {row.original.profileImage ? (
                <img
                  src={row.original.profileImage}
                  alt={row.original.username}
                  className="h-8 w-8 rounded-full object-cover"
                />
              ) : (
                <User className="h-4 w-4 text-primary" />
              )}
            </div>
            <span className="font-medium">{row.original.username}</span>
          </div>
        ),
      },
      {
        accessorKey: 'name',
        header: 'Name',
        cell: ({ row }) => (
          <span>
            {row.original.firstName} {row.original.lastName || ''}
          </span>
        ),
      },
      {
        accessorKey: 'email',
        header: 'Email',
        cell: ({ row }) => (
          <div className="flex items-center gap-1 text-foreground-muted dark:text-foreground-dark-muted">
            <Mail className="h-3 w-3" />
            {row.original.email}
          </div>
        ),
      },
      {
        accessorKey: 'mobile',
        header: 'Mobile',
        cell: ({ row }) => (
          <div className="flex items-center gap-1 text-foreground-muted dark:text-foreground-dark-muted">
            <Phone className="h-3 w-3" />
            {row.original.mobile}
          </div>
        ),
      },
      {
        accessorKey: 'userStatus',
        header: 'Status',
        cell: ({ row }) => {
          const status = row.original.userStatus;
          const variant =
            status === 'ACTIVE'
              ? 'success'
              : status === 'SUSPENDED'
                ? 'warning'
                : status === 'TERMINATED'
                  ? 'danger'
                  : 'default';
          return (
            <Badge variant={variant} dot>
              {status}
            </Badge>
          );
        },
      },
      {
        accessorKey: 'createdDate',
        header: 'Created',
        cell: ({ row }) => (
          <span className="text-foreground-muted dark:text-foreground-dark-muted">
            {formatDate(row.original.createdDate)}
          </span>
        ),
      },
    ],
    []
  );

  return (
    <div className="space-y-6">
      {/* Page Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-foreground dark:text-foreground-dark">
            Platform Users
          </h1>
          <p className="mt-1 text-sm text-foreground-muted dark:text-foreground-dark-muted">
            Manage platform administrator accounts
          </p>
        </div>
        {canCreateUser && (
          <Button onClick={handleCreateUser} leftIcon={<Plus className="h-4 w-4" />}>
            Create User
          </Button>
        )}
      </div>

      {/* Data Table */}
      <Card>
        <CardHeader
          title="User List"
          description="Click on a row to view details"
        />
        <CardBody className="p-0">
          <DataTable
            columns={columns}
            data={data}
            isLoading={isLoading}
            searchable
            searchPlaceholder="Search users..."
            searchValue={searchTerm}
            onSearchChange={handleSearchChange}
            paginated
            totalRows={totalRows}
            pageIndex={pagination.pageIndex}
            pageSize={pagination.pageSize}
            onPaginationChange={handlePaginationChange}
            sortingState={sorting}
            onSortingChange={handleSortingChange}
            onRowClick={handleViewDetails}
            emptyMessage="No users found"
            emptyDescription="There are no platform users yet."
            className="border-0"
          />
        </CardBody>
      </Card>
    </div>
  );
}

export default UsersListPage;
